/**
 * colour-me-product-card.js
 * Custom element for the Colour Me "Also Bought" / collection-grid product cards.
 * Keeps price and variant-input in sync when the user picks a pill option.
 */
(() => {
  if (customElements.get('cm-product-card')) return;

  customElements.define(
    'cm-product-card',
    class extends HTMLElement {
      connectedCallback() {
        this._variants = JSON.parse(this.dataset.variants || '[]');

        this.addEventListener('change', (e) => {
          if (!e.target.matches('input[type="radio"]')) return;
          this._syncVariant();
        });

        // Initialise from the pre-selected variant stored in the picker's JSON blob.
        // This runs after the variant-selects CE has had a chance to set checked states,
        // falling back to reading checked radios if the JSON isn't present.
        this._initFromSelectedVariant();
      }

      _initFromSelectedVariant() {
        // The variant picker outputs a <script data-selected-variant> tag with the
        // full variant object. Use it to set the correct price + variant ID on load.
        const jsonEl = this.querySelector('script[data-selected-variant]');

        if (jsonEl) {
          try {
            const variant = JSON.parse(jsonEl.textContent);
            if (variant) {
              this._applyVariant(variant);
              return;
            }
          } catch (_) { /* fall through */ }
        }

        // Fallback: derive from whichever radios are checked after a paint
        requestAnimationFrame(() => this._syncVariant());
      }

      _syncVariant() {
        const checkedValues = [
          ...this.querySelectorAll('[data-variant-buttons] input[type="radio"]:checked'),
        ].map((i) => i.value);

        if (!checkedValues.length) return;

        const match = this._variants.find((v) =>
          checkedValues.every((opt, idx) => v['option' + (idx + 1)] === opt)
        );

        if (match) this._applyVariant(match);
      }

      _applyVariant(variant) {
        const input = this.querySelector('[data-card-variant-input]');
        if (input) input.value = variant.id;

        const price = this.querySelector('[data-card-price]');
        if (price) price.textContent = this._money(variant.price);

        const btn = this.querySelector('[data-card-add-btn]');
        if (btn) {
          btn.disabled = !variant.available;
          const title = this.querySelector('[data-card-title]')?.textContent?.trim();
          if (title) {
            btn.setAttribute(
              'aria-label',
              variant.available
                ? `Add ${title} – ${this._money(variant.price)}`
                : `${title} – sold out`
            );
          }
        }
      }

      _money(cents) {
        return (cents / 100).toLocaleString('en-GB', {
          style: 'currency',
          currency: 'GBP',
        });
      }
    }
  );

  // "Also Bought" arrows — mobile fallback.
  // The theme's <grid-slider> skips binding click handlers on touch
  // devices (swipe-only assumption), but these arrows are always
  // visible here. If grid-slider's own handler already ran it will
  // have called preventDefault(), so we only step in when it didn't.
  // Also covers the case where no slide is yet tagged .is-visible
  // (falls back to the first slide) since grid-slider's own handler
  // no-ops silently in that state.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cm-also-bought__arrow');
    if (!btn || e.defaultPrevented) return;

    const slider = btn.closest('grid-slider')?.querySelector('[data-grid-slider]');
    if (!slider) return;

    const current = slider.querySelector('[data-grid-item].is-visible') || slider.querySelector('[data-grid-item]');
    if (!current) return;

    const target = btn.hasAttribute('data-button-prev')
      ? current.previousElementSibling
      : current.nextElementSibling;

    if (target) {
      slider.scrollTo({ top: 0, left: target.offsetLeft, behavior: 'smooth' });
    }
  });
})();
